import React, { useEffect, useRef } from 'react'
import { useCallback, useState, useMemo } from 'react'
import { useEventHandlers } from '@react-leaflet/core'
import './index.less'
import 'leaflet/dist/leaflet.css'
import * as L from 'leaflet'
import { MapContainer, TileLayer, useMap, useMapEvent, Rectangle } from 'react-leaflet'
import { useAppSelector } from '@/hooks'
import { background } from '@/features/SetBackgroundColor/setBackgroundColor'
import { SRC_URL } from '@/server/apiConfig'

// bounds样式
const BOUNDS_STYLE = { weight: 2, color: 'red', fill: false, dashArray: '5,2' }

/**
 * 控制minimap中bounds的位置
 * @param parentMap: 瓦片地图
 * @param zoom: 放缩层级
 * @returns rectangle：矩形
 */
function MinimapBounds({ parentMap, zoom }) {
  const minimap = useMap()
  // 点击位置作为bounds的中心位置
  const onClick = useCallback(
    (e) => {
      parentMap.setView(e.latlng, parentMap.getZoom())
    },
    [parentMap]
  )
  useMapEvent('click', onClick)

  // 跟踪瓦片地图的位置，与minimap的bounds位置对应
  const [bounds, setBounds] = useState(parentMap.getBounds())
  const onChange = useCallback(() => {
    setBounds(parentMap.getBounds())
  }, [minimap, parentMap, zoom])
  // 监听瓦片地图位置
  const handlers = useMemo(() => ({ move: onChange, zoom: onChange }), [])
  // @ts-ignore
  useEventHandlers({ instance: parentMap }, handlers)
  return <Rectangle bounds={bounds} pathOptions={BOUNDS_STYLE} />
}
/**
 * @param tileUrl 瓦片地图的url
 * @param zoom 放缩层级
 * @param miniMapRef minimap's ref
 * @returns
 */
function MinimapControl({ tileUrl, zoom, miniMapRef, width, height, levelMax, levelMin }) {
  const parentMap = useMap()
  const mapZoom = zoom || 0
  useEffect(() => {
    if (width === 0 || height === 0) return
    const timer = setTimeout(() => {
      //@ts-ignore
      const minimap = miniMapRef.current
      if (!minimap) return
      minimap.invalidateSize()
      const mapSize = minimap.getSize()
      const fitZoom =
        mapSize.x > 0 && mapSize.y > 0
          ? Math.min(
              levelMax + Math.log2(mapSize.x / width),
              levelMax + Math.log2(mapSize.y / height)
            )
          : -10
      minimap.setMinZoom(Number.isFinite(fitZoom) ? Math.floor(fitZoom) - 1 : -10)
      minimap.setMaxZoom(levelMax)
      const southWest = minimap.unproject([0, height], levelMax)
      const northEast = minimap.unproject([width, 0], levelMax)
      const bounds = L.latLngBounds(southWest, northEast)
      minimap.fitBounds(bounds, {
        animate: false,
        padding: [6, 6]
      })
      const paddedSouthWest = minimap.unproject([-50, height + 50], levelMax)
      const paddedNorthEast = minimap.unproject([width + 50, -50], levelMax)
      minimap.setMaxBounds(L.latLngBounds(paddedSouthWest, paddedNorthEast))
    }, 0)
    return () => {
      clearTimeout(timer)
    }
  }, [tileUrl, width, height, levelMax, levelMin, miniMapRef])
  // 记住minimap，使其不受位置变化的影响
  const minimap = (
    <MapContainer
      //@ts-ignore
      ref={miniMapRef}
      crs={L.CRS.Simple}
      center={{ lat: 0, lng: 0 }}
      zoom={mapZoom}
      minZoom={-10}
      maxZoom={levelMax}
      zoomSnap={0}
      dragging={false}
      doubleClickZoom={false}
      scrollWheelZoom={false}
      attributionControl={false}
      zoomControl={false}
    >
      <TileLayer
        key={tileUrl}
        url={tileUrl}
        tileSize={512}
        noWrap={true}
        minZoom={-10}
        maxZoom={levelMax}
        minNativeZoom={levelMin}
        maxNativeZoom={levelMax}
      />
      <MinimapBounds parentMap={parentMap} zoom={mapZoom} />
    </MapContainer>
  )

  return (
    <div className='minimap-control-container'>
      <div className='leaflet-control leaflet-bar'>{minimap}</div>
    </div>
  )
}
interface MiniMapProps {
  //图片的tilesPath
  tilesPath: string
  //图片的宽
  width: number
  //图片的高
  height: number
  //图片的最大放缩level
  levelMax: number
  //图片的最小放缩level
  levelMin: number
  //miniMap类型：详情页(0)/画作对比(1)
  displayType: number
}
// 瓦片地图组件
function MiniMap(props: MiniMapProps) {
  const { tilesPath, width, height, levelMax, levelMin, displayType } = props
  // 控制瓦片地图显示url
  const [tileUrl, setTileUrl] = useState<string>('')
  // 获取背景颜色
  const backgroundColor = useAppSelector((state) => {
    if (displayType === 0) return state.backgroundColor.detailsPageBackground
    else return state.backgroundColor.analysisBackground
  })
  useEffect(() => {
    setTileUrl(SRC_URL + tilesPath + '/{z}/{x}/{y}.png')
  }, [tilesPath])
  // 瓦片地图ref
  const mapRef = useRef()
  // minimap's ref
  const miniMapRef = useRef()
  // 当画作改变时，动态调整瓦片地图和minimap的限制范围
  useEffect(() => {
    if (width === 0 || height === 0) return
    const timer = setTimeout(() => {
      //大图范围限制
      //@ts-ignore
      mapRef.current?.setMaxZoom(levelMax)
      //@ts-ignore
      const southWest = mapRef.current?.unproject(
        [-500, height + 500],
        //@ts-ignore
        mapRef.current?.getMaxZoom()
      )
      //@ts-ignore
      const northEast = mapRef.current?.unproject(
        [width + 500, -500],
        //@ts-ignore
        mapRef.current?.getMaxZoom()
      )
      //@ts-ignore
      mapRef.current?.setMaxBounds(L.latLngBounds(southWest, northEast))
    }, 0)
    return () => {
      clearTimeout(timer)
    }
  }, [width, height, levelMax, levelMin])
  return (
    <div
      className={'minimap-container ' + (displayType === 0 ? 'minimap-container-detail' : '')}
      style={{
        backgroundImage:
          backgroundColor === background.SUN
            ? 'linear-gradient(to bottom, rgb(203, 198, 198), rgb(48, 45, 45))'
            : '',
        backgroundColor: backgroundColor === background.SUN ? '' : 'black'
      }}
    >
      <MapContainer
        //@ts-ignore
        ref={mapRef}
        crs={L.CRS.Simple}
        center={{ lat: 0, lng: 0 }}
        zoom={levelMin}
        style={{ height: '100%', width: '100%' }}
        dragging={true}
        doubleClickZoom={true}
        scrollWheelZoom={true}
        attributionControl={false}
        zoomControl={false}
        minZoom={levelMin}
        maxZoom={levelMax}
      >
        <TileLayer key={tileUrl} url={tileUrl} tileSize={512} noWrap={true} />
        <div style={{ display: displayType === 0 ? 'block' : 'none' }}>
          <MinimapControl
            tileUrl={tileUrl}
            zoom={levelMin}
            miniMapRef={miniMapRef}
            width={width}
            height={height}
            levelMax={levelMax}
            levelMin={levelMin}
          />
        </div>
      </MapContainer>
    </div>
  )
}
export default React.memo(MiniMap)
